import { Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { toSegments } from "../helpers/mentions.ts"
import type { Participant } from "../types/messaging.types.ts"

interface MessageBodyProps {
  body: string
  isDeleted: boolean
  participants: Participant[]
  mentionNames?: Record<string, string>
  highlight?: string
}

/**
 * Renders one message's text: mentions as chips, URLs as links, everything
 * else as plain text. `white-space: pre-wrap` is what preserves the line
 * breaks a multiline composer produced.
 */
export function MessageBody({
  body,
  isDeleted,
  participants,
  mentionNames,
  highlight
}: MessageBodyProps) {
  const { t } = useTranslation()

  if (isDeleted) {
    return (
      <Typography.Text italic type={"secondary"}>
        {t("messages.deletedPlaceholder", "This message was deleted.")}
      </Typography.Text>
    )
  }

  const segments = toSegments(body, participants, mentionNames)

  return (
    <span className={"block whitespace-pre-wrap [overflow-wrap:anywhere]"}>
      {segments.map((segment, index) => {
        if (segment.kind === "mention") {
          return (
            <span
              key={`${segment.userId}-${index}`}
              className={
                "rounded px-1 font-medium text-blue-700 bg-blue-50 dark:text-blue-300"
              }
            >
              {segment.label}
            </span>
          )
        }
        if (segment.kind === "link") {
          return (
            <a
              key={`${segment.href}-${index}`}
              href={segment.href}
              target={"_blank"}
              rel={"noopener noreferrer"}
              className={"text-blue-600 underline"}
            >
              {segment.label}
            </a>
          )
        }
        if (!highlight) {
          return <React.Fragment key={index}>{segment.value}</React.Fragment>
        }
        return (
          <React.Fragment key={index}>
            {highlightMatches(segment.value, highlight)}
          </React.Fragment>
        )
      })}
    </span>
  )
}

/** Marks every occurrence of the search term, so a jumped-to hit is findable. */
function highlightMatches(text: string, term: string): React.ReactNode {
  const lower = text.toLowerCase()
  const needle = term.toLowerCase()
  if (!needle || !lower.includes(needle)) return text

  const parts: React.ReactNode[] = []
  let cursor = 0
  let found = lower.indexOf(needle, cursor)
  while (found !== -1) {
    if (found > cursor) parts.push(text.slice(cursor, found))
    parts.push(
      <mark key={`${found}`} className={"bg-orange-100 px-0.5"}>
        {text.slice(found, found + needle.length)}
      </mark>
    )
    cursor = found + needle.length
    found = lower.indexOf(needle, cursor)
  }
  if (cursor < text.length) parts.push(text.slice(cursor))
  return parts
}
